import React, { useCallback } from 'react';
import range from 'lodash/range';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { Box, Input, Text, Flex, Select } from 'roo-ui/components';
import { FieldLabel, FieldError } from 'components/Form';
import useMastercardSession from './useMastercardSession';
import { updatePayments } from 'store/checkout/checkoutActions';
import Loader from 'components/Loader';
import LoadingError from './LoadingError/LoadingError';
import LoaderSkeleton from './LoaderSkeleton';
import SecurityCodeModal from './SecurityCodeModal';
import { MONTHS } from 'lib/enums/checkout';

const CURRENT_YEAR = new Date().getFullYear();
export const YEARS = range(CURRENT_YEAR, CURRENT_YEAR + 10);

const Wrapper = styled(Box)`
  iframe {
    margin-bottom: 0.75rem !important;
    display: block;
  }
`;

const StyledSelect = styled(Select)`
  ::-ms-expand {
    display: none;
  }
`;

const MastercardForm = ({ ...rest }) => {
  const dispatch = useDispatch();

  const updateCreditCard = useCallback(
    (creditCard) => {
      dispatch(updatePayments({ cash: { creditCard } }));
    },
    [dispatch],
  );

  const { setFieldValue, getFieldError, isLoading, isUpdating, hasLoadingError, formKey, reload } = useMastercardSession({
    updateCreditCard,
    cardFields: {
      number: '#cardNumber',
      securityCode: '#securityCode',
      expiryMonth: '#expiryMonth',
      expiryYear: '#expiryYear',
    },
  });

  return (
    <Wrapper {...rest} data-is-updating={isUpdating}>
      {hasLoadingError && <LoadingError reload={reload} />}
      {!hasLoadingError && (
        <Loader isLoading={isLoading} loaderComponent={LoaderSkeleton} key={formKey}>
          <Flex flexDirection={['column', 'row']} mb={[0, 6]}>
            <FieldLabel htmlFor="cardNumber" width={['100%', '40%']} pr={[0, 4]}>
              Card number
              <Input id="cardNumber" error={!!getFieldError('cardNumber')} readOnly />
              <FieldError error={getFieldError('cardNumber')} data-testid="card-number-error" />
              <Text display="block" lineHeight="normal" color="greys.dusty">
                We accept Mastercard, Visa or American Express
              </Text>
            </FieldLabel>
            <FieldLabel htmlFor="nameOnCard" width={['100%', '60%']}>
              Name on card
              <Input
                id="nameOnCard"
                name="nameOnCard"
                error={!!getFieldError('nameOnCard')}
                onChange={(e) => setFieldValue({ name: 'nameOnCard', value: e.target.value })}
              />
              <FieldError error={getFieldError('nameOnCard')} data-testid="name-on-card-error" />
            </FieldLabel>
          </Flex>
          <Flex flexDirection={['column', 'row']} mb={6}>
            <FieldLabel htmlFor="expiryMonth" width={['100%', '40%']} pr={[0, 4]}>
              Expiry month
              <StyledSelect
                id="expiryMonth"
                error={!!getFieldError('expiryMonth')}
                onChange={(e) => setFieldValue({ name: 'expiryMonth', value: e.target.value })}
              >
                <option value="">Select month</option>
                {MONTHS.map((monthName, index) => (
                  <option key={monthName} value={index + 1}>
                    {monthName}
                  </option>
                ))}
              </StyledSelect>
              <FieldError error={getFieldError('expiryMonth')} data-testid="expiry-month-error" />
            </FieldLabel>
            <FieldLabel htmlFor="expiryYear" width={['100%', '40%']} pr={[0, 4]}>
              Expiry year
              <StyledSelect
                id="expiryYear"
                error={!!getFieldError('expiryYear')}
                onChange={(e) => setFieldValue({ name: 'expiryYear', value: e.target.value })}
              >
                <option value="">Select year</option>
                {YEARS.map((expiryYear) => (
                  <option key={expiryYear} value={expiryYear}>
                    {expiryYear}
                  </option>
                ))}
              </StyledSelect>
              <FieldError error={getFieldError('expiryYear')} data-testid="expiry-year-error" />
            </FieldLabel>
            <FieldLabel htmlFor="securityCode" width={['100%', '20%']}>
              CVV number
              <Input id="securityCode" error={!!getFieldError('securityCode')} readOnly />
              <FieldError error={getFieldError('securityCode')} data-testid="security-code-error" />
              <SecurityCodeModal />
            </FieldLabel>
          </Flex>
        </Loader>
      )}
    </Wrapper>
  );
};

export default MastercardForm;
